import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CircleArrowUp as ArrowUpCircle, Check, Loader as Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import type { Theme, BulkActionProgress } from '../types';

interface ThemesTabProps {
  themes: Theme[];
  selectedThemes: string[];
  onToggleSelection: (stylesheet: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  actionLoading: string | null;
  bulkActionProgress: BulkActionProgress | null;
  onThemeAction: (action: string, stylesheet: string) => Promise<void>;
  onBulkAction: (items: string[], action: (slug: string) => Promise<void>, label: string) => Promise<void>;
  onOpenChangelog: (type: string, slug: string, name: string) => void;
  isSyncing: boolean;
}

export const ThemesTab: React.FC<ThemesTabProps> = ({
  themes,
  selectedThemes,
  onToggleSelection,
  onSelectAll,
  onDeselectAll,
  actionLoading,
  onThemeAction,
  onBulkAction,
  onOpenChangelog,
  isSyncing,
}) => {
  if (isSyncing && themes.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (themes.length === 0) {
    return <p className="text-center text-sm text-muted-foreground py-10">No themes found.</p>;
  }

  return (
    <div className="flex flex-col max-h-[600px]">
      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {themes.map((theme) => {
          const isSelected = selectedThemes.includes(theme.stylesheet);
          return (
            <div key={theme.stylesheet} className={`flex items-center justify-between px-5 py-3.5 transition-colors relative ${isSelected ? 'bg-workspace-accent/5' : 'hover:bg-muted/10'}`}>
              <div className="flex-1 flex items-center justify-between min-w-0">
                <div className="flex items-center gap-4 min-w-0">
                  <input
                    type="checkbox"
                    className="rounded border-border text-workspace-accent focus:ring-workspace-accent"
                    checked={isSelected}
                    onChange={() => onToggleSelection(theme.stylesheet)}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground truncate">{theme.name}</p>
                      {theme.is_child && <span className="text-[8px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-bold uppercase tracking-wider border border-border">Child</span>}
                      <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${theme.is_active
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/50'
                          : 'bg-muted text-muted-foreground border border-border'
                        }`}>
                        {theme.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 ml-4">
                  <div className="flex items-center gap-2">
                    {actionLoading?.startsWith(`theme-${theme.stylesheet}-`) && (
                      <div className="w-10 flex-shrink-0 animate-pulse">
                        <Progress value={undefined} className="h-1 bg-workspace-accent/10" />
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground bg-muted/30 px-2.5 py-1 rounded-full border border-border/50">
                      {theme.has_update ? (
                        <div className="flex items-center gap-1.5 leading-none">
                          <span className="line-through opacity-40 font-normal">v{theme.version}</span>
                          <ArrowRight className="w-2.5 h-2.5 opacity-40" />
                          <span className="text-amber-600 dark:text-amber-400 font-bold">v{theme.new_version}</span>
                        </div>
                      ) : (
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold leading-none">v{theme.version}</span>
                      )}
                    </div>
                    <button
                      onClick={() => onOpenChangelog('theme', theme.stylesheet, theme.name)}
                      className="text-[10px] font-bold text-workspace-accent hover:text-workspace-accent-hover hover:underline uppercase tracking-wider px-2 py-1 rounded transition-colors"
                    >
                      Changelog
                    </button>
                  </div>

                  {theme.has_update ? (
                    <div className="flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                      Update Available
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                      <Check className="w-3 h-3" /> Up to date
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {selectedThemes.length > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="bg-slate-900 text-white p-3 flex items-center justify-between sticky bottom-0 z-20"
          >
            <div className="flex items-center gap-3 ml-2">
              <span className="text-xs font-bold">{selectedThemes.length} selected</span>
              <div className="w-px h-3 bg-slate-700 mx-1" />
              <button onClick={onSelectAll} className="text-[10px] font-bold text-workspace-accent hover:text-workspace-accent-hover transition-colors uppercase tracking-wider">
                Select All
              </button>
              <button onClick={onDeselectAll} className="text-[10px] font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-wider">
                {selectedThemes.length === 1 ? 'Deselect' : 'Deselect All'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              {selectedThemes.some(slug => themes.find(t => t.stylesheet === slug)?.has_update) && (
                <button
                  onClick={async () => {
                    const itemsToUpdate = selectedThemes.filter(slug => themes.find(t => t.stylesheet === slug)?.has_update);
                    if (itemsToUpdate.length > 0) {
                      if (window.confirm(`Update ${itemsToUpdate.length === 1 ? 'the selected theme' : `${itemsToUpdate.length} themes`}?`)) {
                        await onBulkAction(itemsToUpdate, (slug) => onThemeAction('update', slug), 'Updating Themes');
                        onDeselectAll();
                      }
                    }
                  }}
                  className="px-3 py-1.5 bg-workspace-accent rounded-lg text-xs font-bold hover:bg-workspace-accent-hover transition-colors flex items-center gap-2"
                >
                  <ArrowUpCircle className="w-3.5 h-3.5" /> {selectedThemes.length === 1 ? 'Update' : 'Bulk Update'}
                </button>
              )}
              <div className="w-px h-4 bg-slate-700 mx-1" />
              {selectedThemes.some(s => !themes.find(t => t.stylesheet === s)?.is_active) && (
                <button
                  onClick={async () => {
                    const itemsToActivate = selectedThemes.filter(s => !themes.find(t => t.stylesheet === s)?.is_active);
                    if (itemsToActivate.length > 0) {
                      await onBulkAction(itemsToActivate, (s) => onThemeAction('activate', s), 'Activating Themes');
                      onDeselectAll();
                    }
                  }}
                  className="px-3 py-1.5 bg-slate-800 rounded-lg text-xs font-semibold hover:bg-slate-700 transition-colors"
                >
                  Activate
                </button>
              )}
              <button
                onClick={async () => {
                  if (window.confirm(`Delete ${selectedThemes.length} themes? This action cannot be undone.`)) {
                    await onBulkAction(selectedThemes, (s) => onThemeAction('delete', s), 'Deleting Themes');
                    onDeselectAll();
                  }
                }}
                className="px-3 py-1.5 bg-red-900/40 text-red-400 border border-red-900/60 rounded-lg text-xs font-semibold hover:bg-red-900/60 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
